import { flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import React, { useMemo } from 'react';
import './applicationTable.scss';

/**
 * ApplicationTable Component
 * 
 * This component renders a table using the `@tanstack/react-table` library. It takes in data and column definitions
 * as props and displays them in a tabular format. Additionally, it provides click functionality to trigger specific
 * functions based on row type and column header.
 * 
 * @component
  * @memberof Components
 * @param {Object} props - The properties passed to the component.
 * @param {Array} props.data - The table data containing objects with required fields.
 * @param {Array} props.columns - The column definitions for the table.
 * 
 * @example
 * const data = [
 *   { type: 'New Connection', function: handleNewConnection },
 *   { type: 'Change of Meter', function: handleChangeOfMeter },
 * ];
 * 
 * const columns = [
 *   { accessorKey: 'type', header: 'Application Type' },
 *   { accessorKey: 'pending', header: 'Pending' },
 * ];
 * 
 * <ApplicationTable data={data} columns={columns} />
 */
const ApplicationTable = (props) => {

    // Memoize the data to prevent unnecessary re-renders
    const data = useMemo(() => {
        return props.data;
    }, [props.data]);

    // Memoize the columns for better performance
    const columns = useMemo(() => {
        return props.columns;
    }, [props.columns]);

    // Initialize the table with data, columns, and row model
    const table = useReactTable({ data, columns, getCoreRowModel: getCoreRowModel() });

    /**
     * Handles the click event on a table row cell.
     * Triggers the appropriate function based on row type and column name.
     * 
     * @param {Object} rowData - The data of the clicked row.
     * @param {string} columnName - The name of the clicked column.
     */
    const handleRowClick = (rowData, columnName) => {
        switch (rowData.type) {
            case 'New Connection':
                props.data[0].function(rowData);
                break;
            case 'Change of Meter':
                props.data[1].function(rowData);
                break;
            case 'Power Outage Complaints':
                props.data[2].function(rowData);
                break;
            case 'Billing Complaints':
                props.data[3].function(rowData);
                break;
            case 'Defective Meter (CP-90)':
                props.data[4].function(rowData);
                break;
            case 'T & D':
                switch (columnName) {
                    case 'Monthly':
                        props.data[0].functionMonthly(rowData);
                        break;
                    case 'Progressive':
                        props.data[0].functionProgressive(rowData);
                        break
                    default:
                        break;
                }
                break;
            case 'Recovery':
                switch (columnName) {
                    case 'Monthly':
                        props.data[1].functionMonthly(rowData);
                        break;
                    case 'Progressive':
                        props.data[1].functionProgressive(rowData);
                        break
                    default:
                        break;
                }
                break;
            case 'AT & C':
                switch (columnName) {
                    case 'Monthly':
                        props.data[2].functionMonthly(rowData);
                        break;
                    case 'Progressive':
                        props.data[2].functionProgressive(rowData);
                        break
                    default:
                        break;
                }
                break;
            default:
                break;
        }
    };

    return (
        <div className='tbl-container'>
            <table className='table'>
                <thead className='table-header'>
                    {table.getHeaderGroups().map((headerGroup, index) => (
                        <tr key={index} className='header-row' >
                            {headerGroup.headers.map((header, index) => (
                                <th key={index} className='header-cell' style={{ textAlign: header.column.columnDef.header === "Pending" || header.column.columnDef.header === "Attended" || header.column.columnDef.header === "Total" || header.column.columnDef.header === "Monthly" || header.column.columnDef.header === "Progressive" ? "center" : "left" }}>
                                    <p className='cell-text'>
                                        {flexRender(
                                            header.column.columnDef.header,
                                            header.getContext()
                                        )}
                                    </p>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody className='table-body'>
                    {table.getCoreRowModel().rows.map((row, index) => (
                        <tr key={index} className='body-row' style={{ backgroundColor: index % 2 ? '#d5e5ff' : '#f2f4f6' }}>
                            {row.getVisibleCells().map((cell, index) => (
                                <td key={index}

                                    className='body-cell'
                                    style={{
                                        textAlign: cell.column.columnDef.header === 'Pending' || cell.column.columnDef.header === 'Attended' || cell.column.columnDef.header === 'Total' || cell.column.columnDef.header === 'Monthly' || cell.column.columnDef.header === 'Progressive' ? 'center' : 'left'
                                    }}>
                                    <p className='cell-text' onClick={() => handleRowClick(row.original, cell.column.columnDef.header)} style={{
                                        color: cell.column.columnDef.header === 'Pending' ? 'red' : cell.column.columnDef.header === "Attended" ? '#019760' :
                                            cell.column.columnDef.header === 'Monthly' ?
                                                row.getVisibleCells()[0].getValue() === "T & D" ? row.getVisibleCells()[1].getValue() > 0 ? 'red' : '#019760' :
                                                    row.getVisibleCells()[0].getValue() === "Recovery" ? row.getVisibleCells()[1].getValue() > 0 ? '#019760' : 'red' :
                                                        row.getVisibleCells()[0].getValue() === "AT & C" ? row.getVisibleCells()[1].getValue() > 0 ? 'red' : '#019760' : '' :
                                                cell.column.columnDef.header === 'Progressive' ?
                                                    row.getVisibleCells()[0].getValue() === "T & D" ? row.getVisibleCells()[2].getValue() > 0 ? 'red' : '#019760' :
                                                        row.getVisibleCells()[0].getValue() === "Recovery" ? row.getVisibleCells()[2].getValue() > 0 ? '#019760' : 'red' :
                                                            row.getVisibleCells()[0].getValue() === "AT & C" ? row.getVisibleCells()[2].getValue() > 0 ? 'red' : '#019760' : '' : ''
                                    }}>
                                        {
                                            flexRender(cell.column.columnDef.cell, cell.getContext())
                                        }
                                        {
                                            (cell.column.columnDef.header === "Monthly" || cell.column.columnDef.header === "Progressive") && '%'
                                        }
                                    </p>
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

}

export default ApplicationTable
